import { useRef } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import type { MouseEvent, ReactNode, Ref } from 'react'
import { useSession } from '../session'

/**
 * App shell: a scrolling screen area and a tab bar underneath it.
 *
 * Bottom navigation rather than a drawer because this is used one-handed on a
 * phone, and in standalone mode there is no browser back button to fall back on.
 *
 * The tab bar is a flex sibling of the scroll area, not `position: fixed`. Fixed
 * is anchored to the layout viewport, which on iOS is not where the bottom of the
 * screen is: the URL bar collapsing and the on-screen keyboard both move it, so a
 * fixed bar drifts up the page or hides behind the keyboard. A flex column on
 * `100dvh` with the overflow on `<main>` puts the bar at the bottom structurally,
 * where nothing can shift it -- and gives screens a real scroll container, so a
 * `sticky` footer like 記帳's 儲存 button works without measuring anything.
 */

const TABS = [
  { to: '/', label: '本月', icon: '◎' },
  { to: '/reports', label: '報表', icon: '◔' },
  { to: '/add', label: '記帳', icon: '＋' },
  { to: '/members', label: '分帳', icon: '⇄' },
  { to: '/import', label: '匯入', icon: '↥' },
]

export function Layout() {
  const location = useLocation()
  const primerRef = useRef<HTMLInputElement>(null)

  return (
    <div className="mx-auto flex h-dvh max-w-md flex-col">
      <main
        key={location.pathname}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-6"
      >
        <Outlet />
      </main>

      <nav className="safe-bottom shrink-0 border-t border-ink-line/60 bg-ink">
        <ul className="grid grid-cols-5">
          {TABS.map((tab) => (
            <li key={tab.to}>
              <NavLink
                to={tab.to}
                end={tab.to === '/'}
                // Opens the keyboard on the way to 記帳; see `KeyboardPrimer`. The
                // `mousedown` default is suppressed so the desktop behaviour of
                // focusing the link itself cannot immediately take the focus back
                // off the primer -- it does not prevent the click or the navigation.
                {...(tab.to === '/add'
                  ? {
                      onPointerDown: () => primerRef.current?.focus(),
                      onMouseDown: (e: MouseEvent) => e.preventDefault(),
                    }
                  : {})}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 py-2 text-xs ${
                    isActive ? 'text-accent' : 'text-muted'
                  }`
                }
              >
                <span aria-hidden className="text-lg leading-none">
                  {tab.icon}
                </span>
                {tab.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <KeyboardPrimer ref={primerRef} />
    </div>
  )
}

/**
 * An input whose only job is to open the on-screen keyboard.
 *
 * iOS raises the keyboard only for a `focus()` that happens inside the user's own
 * gesture, and the 記帳 amount field does not exist yet when the tab is tapped --
 * which is why its `autoFocus` moves the cursor there but leaves the keyboard
 * down. Focusing this one on `pointerdown` opens the keyboard while the gesture is
 * still live; `AddExpense` then takes focus off it, and iOS keeps the keyboard up
 * across a move between two text inputs.
 *
 * It has to be rendered and in the viewport -- iOS ignores `display: none`,
 * `visibility: hidden` and `readonly` -- so it is invisible instead, and untappable
 * so it can never take a keystroke from a real field.
 */
function KeyboardPrimer({ ref }: { ref: Ref<HTMLInputElement> }) {
  return (
    <input
      ref={ref}
      aria-hidden
      tabIndex={-1}
      // The same keypad the amount field asks for, so priming it does not flash a
      // full alphabetic keyboard first.
      inputMode="decimal"
      value=""
      onChange={() => undefined}
      className="pointer-events-none fixed bottom-0 left-0 size-px opacity-0"
    />
  )
}

export function Screen({
  title,
  action,
  children,
}: {
  title: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  const { pathname } = useLocation()
  return (
    <div className="safe-top px-4">
      <header className="mb-4 flex items-center justify-between gap-3">
        <h1 className="min-w-0 truncate text-xl font-semibold">{title}</h1>
        <div className="flex shrink-0 items-center gap-3">
          {action}
          {/* Not on the account screen itself, where it would link to the page
              already open. */}
          {pathname === '/profile' ? null : <AccountButton />}
        </div>
      </header>
      {children}
    </div>
  )
}

/**
 * The way to the account screen, in every screen's top-right corner.
 *
 * Here rather than in the shell above, because the shell has no top bar -- each
 * screen draws its own header -- and a fixed overlay in that corner would land on
 * top of the per-screen `action` that already lives there. Sharing the row instead
 * means the two cannot collide.
 *
 * It shows an initial rather than a generic glyph: the pair may share a tablet, and
 * "whose ledger am I looking at" is worth answering without a tap.
 */
function AccountButton() {
  const { session } = useSession()
  const initial = (session?.displayName ?? session?.email ?? '?').trim().charAt(0).toUpperCase()
  return (
    <NavLink
      to="/profile"
      aria-label="我的帳號"
      className="flex size-8 items-center justify-center rounded-full bg-ink-soft text-sm font-semibold ring-1 ring-ink-line"
    >
      <span aria-hidden>{initial}</span>
    </NavLink>
  )
}
